import React, { useState } from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import { ZoomIn, ZoomOut, RotateCcw, Maximize2 } from 'lucide-react';

const ZoomableImage = ({ 
  src, 
  alt = "Zoomable image", 
  width = "100%", 
  height = "500px",
  className = "" 
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isSvg, setIsSvg] = useState(false);

  // Check if the image is SVG
  React.useEffect(() => {
    if (src) {
      const checkIfSvg = src.toLowerCase().includes('.svg') || 
                        src.toLowerCase().includes('data:image/svg') ||
                        src.toLowerCase().includes('svg+xml');
      setIsSvg(checkIfSvg);
      setImageLoaded(false);
    }
  }, [src]);

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform, centerView } = useControls();
    
    return (
      <div style={styles.controlsContainer}>
        <button
          onClick={() => zoomIn(0.3)}
          style={{...styles.controlButton, ...styles.zoomInButton}}
          title="Zoom In"
        >
          <ZoomIn style={styles.buttonIcon} />
        </button>
        
        <button
          onClick={() => zoomOut(0.3)}
          style={{...styles.controlButton, ...styles.zoomOutButton}}
          title="Zoom Out"
        >
          <ZoomOut style={styles.buttonIcon} />
        </button>
        
        <button
          onClick={() => {
            resetTransform();
            centerView();
          }}
          style={{...styles.controlButton, ...styles.resetButton}}
          title="Reset View"
        >
          <RotateCcw style={styles.buttonIcon} />
        </button>
        
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          style={{...styles.controlButton, ...styles.fullscreenButton}}
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          <Maximize2 style={styles.buttonIcon} />
        </button>
      </div>
    );
  };

  const containerStyle = {
    ...styles.container,
    ...(isFullscreen ? styles.fullscreenContainer : {}),
    width: isFullscreen ? 'auto' : '100%',
    maxWidth: isFullscreen ? 'none' : '100%',
    height: isFullscreen ? 'auto' : height
  };

  return (
    <>
      {isFullscreen && (
        <div 
          style={styles.fullscreenOverlay}
          onClick={() => setIsFullscreen(false)}
        />
      )}
      
      <div 
        style={containerStyle}
        className={className}
      >
        <TransformWrapper
          initialScale={1}
          minScale={0.1}
          maxScale={isSvg ? 20 : 5}
          doubleClick={{
            disabled: false,
            mode: "zoomIn",
            step: 0.7,
            animationTime: 300
          }}
          wheel={{
            step: isSvg ? 0.2 : 0.1,
            smoothStep: isSvg ? 0.01 : 0.005
          }}
          pinch={{
            step: isSvg ? 10 : 5
          }}
          panning={{
            velocityDisabled: true
          }}
          limitToBounds={false}
          centerOnInit={true}
        >
          <Controls />
          <TransformComponent
            wrapperStyle={styles.transformWrapper}
            contentStyle={styles.transformContent}
          >
            {isSvg ? (
              <div style={styles.svgContainer}>
                <img
                  src={src}
                  alt={alt}
                  style={styles.svgImage}
                  draggable={false}
                  onLoad={() => setImageLoaded(true)}
                />
              </div>
            ) : (
              <img
                src={src}
                alt={alt}
                style={styles.regularImage}
                draggable={false}
                onLoad={() => setImageLoaded(true)}
              />
            )}
          </TransformComponent>
        </TransformWrapper>
        
        {/* Help indicator */}
        <div style={styles.helpIndicator}>
          <div style={styles.helpText}>
            <span style={styles.helpEmoji}>✨</span>
            <span>Double-click to zoom • Scroll wheel • Drag to pan</span>
          </div>
        </div>
        
        {/* Close fullscreen overlay */}
        {isFullscreen && (
          <button
            onClick={() => setIsFullscreen(false)}
            style={styles.closeButton}
            title="Exit Fullscreen"
          >
            <svg style={styles.closeIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </>
  );
};

const styles = {
  container: {
    position: 'relative',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 50%, #f3e8ff 100%)',
  },
  fullscreenContainer: {
    position: 'fixed',
    top: '16px',
    left: '16px',
    right: '16px',
    bottom: '16px',
    zIndex: 50,
    background: 'linear-gradient(135deg, #111827 0%, #1e3a8a 50%, #581c87 100%)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
  },
  fullscreenOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(4px)',
    zIndex: 40,
  },
  controlsContainer: {
    position: 'absolute',
    top: '24px',
    right: '24px',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '12px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
  controlButton: {
    padding: '12px',
    borderRadius: '12px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
    outline: 'none',
  },
  zoomInButton: {
    background: 'linear-gradient(135deg, #ea6347 0%, #ea6347 100%)',
  },
  zoomOutButton: {
    background: 'linear-gradient(135deg, #ea6347 0%, #ea6347 100%)',
  },
  resetButton: {
    background: 'linear-gradient(135deg, #ea6347 0%, #ea6347 100%)',
  },
  fullscreenButton: {
    background: 'linear-gradient(135deg, #ea6347 0%, #ea6347 100%)',
  },
  buttonIcon: {
    width: '20px',
    height: '20px',
    color: 'white',
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
  },
  transformWrapper: {
    width: '100%',
    height: '100%',
  },
  transformContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  svgContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minWidth: '200px',
    minHeight: '200px',
  },
  svgImage: {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'contain',
    userSelect: 'none',
    transition: 'filter 0.3s ease',
  },
  regularImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    userSelect: 'none',
    transition: 'filter 0.3s ease',
  },
  helpIndicator: {
    position: 'absolute',
    bottom: '24px',
    left: '24px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '12px 16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
  helpText: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  helpEmoji: {
    fontSize: '18px',
  },
  closeButton: {
    position: 'absolute',
    top: '24px',
    left: '24px',
    zIndex: 10,
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(17, 24, 39, 0.8) 100%)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    color: 'white',
    transition: 'all 0.3s ease',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    cursor: 'pointer',
    outline: 'none',
  },
  closeIcon: {
    width: '24px',
    height: '24px',
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
  },
};

// Additional styles for the demo app
Object.assign(styles, {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
    padding: '24px',
  },
  appContent: {
    maxWidth: '1024px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '8px',
    margin: 0,
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '16px',
    margin: 0,
  },
  imageSelector: {
    marginBottom: '32px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '16px',
  },
  selectorButton: {
    padding: '12px 24px',
    borderRadius: '16px',
    border: '2px solid',
    transition: 'all 0.3s ease',
    fontWeight: '500',
    cursor: 'pointer',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
    outline: 'none',
  },
  selectorButtonActive: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)',
    color: 'white',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  selectorButtonInactive: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#374151',
    borderColor: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(4px)',
  },
  instructionsContainer: {
    marginTop: '40px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
  instructionsTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 0 24px 0',
  },
  instructionsEmoji: {
    fontSize: '24px',
  },
  instructionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    color: '#374151',
  },
  desktopInstructions: {
    background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #c7d2fe',
  },
  mobileInstructions: {
    background: 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #d8b4fe',
  },
  instructionsSubtitle: {
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 0 16px 0',
  },
  instructionsSubEmoji: {
    fontSize: '18px',
  },
  instructionsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  instructionsItem: {
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  bullet: {
    width: '8px',
    height: '8px',
    backgroundColor: '#3b82f6',
    borderRadius: '50%',
    flexShrink: 0,
  },
});

export default ZoomableImage;